<script
	module
	lang="ts"
>
	/** Set when closing the narrow-screen detail modal saves or discards the part, since that reloads the screen and the new copy would otherwise open the modal again */
	let keepDetailClosedOnReload = false
</script>

<script lang="ts">
	import type { i18n, Mediator, SvelteAsr } from 'types/common'
	import type {
		TeardownFrequentlyUsedInventoryType,
		TeardownInventory,
		TeardownInventoryType,
		TeardownTemplate,
		TeardownVehicle,
	} from './teardown'
	import type { PartScreenData } from 'utility/load-part-screen-data'
	import type { TeardownItem } from 'utility/teardown-list'

	import { getContext } from 'svelte'
	import { graphql } from '$houdini'
	import Icon from '@isoftdata/svelte-icon'
	import Button from '@isoftdata/svelte-button'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import { Badge } from '@isoftdata/svelte-badge'
	import { format as formatCurrency } from '@isoftdata/utility-currency'
	import financialNumber from 'financial-number'
	import { hasPermission } from 'utility/permission'
	import { loadPartScreenData } from 'utility/load-part-screen-data'
	import { buildTeardownItems, partKey } from 'utility/teardown-list'
	import InventoryListBase from 'components/InventoryListBase.svelte'
	import TemplateSelect from 'components/TemplateSelect.svelte'
	import NewPartModal from 'components/NewPartModal.svelte'
	import PartEditor from '../../part/PartEditor.svelte'

	interface Props {
		asr: SvelteAsr
		vehicle: TeardownVehicle
		templates: Array<TeardownTemplate>
		templateId: number | null
		inventoryTypes: Array<TeardownInventoryType>
		frequentlyUsedInventoryTypes: Array<TeardownFrequentlyUsedInventoryType>
		canViewCost: boolean
		/** The key of the item that should be selected when the screen opens */
		selectedItem?: string
	}

	/** What gets loaded when an item is selected */
	type LoadResult =
		| { kind: 'part'; partData: PartScreenData }
		| { kind: 'forbidden' }
		| { kind: 'slot' }
		| { kind: 'error'; message: string }
	type Item = TeardownItem<TeardownInventory>

	let {
		asr,
		vehicle,
		templates,
		templateId: initialTemplateId,
		inventoryTypes,
		frequentlyUsedInventoryTypes,
		canViewCost,
		selectedItem: initialSelectedKey,
	}: Props = $props()

	let list: InventoryListBase<Item, LoadResult> | undefined = $state()
	let editor: PartEditor | undefined = $state()
	// These are only the starting values, the screen reloads itself when it needs to pick them up again
	// svelte-ignore state_referenced_locally
	let selectedKey: string | undefined = $state(initialSelectedKey)
	// svelte-ignore state_referenced_locally
	let templateId = $state(initialTemplateId)
	let hideUnavailable = $state(true)
	let isCreatingPart = $state(false)
	let newPartModal: NewPartModal | undefined = $state()

	const { t: translate } = getContext<i18n>('i18next')
	const mediator = getContext<Mediator>('mediator')

	/** Set when saving the part in order to move somewhere else, so that the save doesn't reload this screen */
	let navigatingAway = false
	/** The item to select after the screen reloads from a save */
	let keyAfterSave: string | undefined
	// Read once, so only the copy of the screen right after the reload keeps the modal closed
	const openDetailOnMount = !keepDetailClosedOnReload
	keepDetailClosedOnReload = false

	const createInventoryMutation = graphql(`
		mutation TeardownCreateInventory($input: NewInventory!) {
			createInventory(input: $input) {
				inventoryId
				storeId
			}
		}
	`)

	function getInventoryTypeName(inventoryTypeId: number): string {
		return `${inventoryTypeId} - ${inventoryTypeMap.get(inventoryTypeId)?.name ?? translate('common:unknownInventoryType', 'Unknown Inventory Type')}`
	}

	function getFilterText(item: Item): string {
		const text = [getInventoryTypeName(item.inventoryTypeId)]
		if (item.kind === 'part') {
			text.push(item.part.tagNumber, item.part.manufacturer?.name ?? '', item.part.model?.name ?? '')
		}
		return text.join(' ')
	}

	function getStatusIcon(item: Item) {
		const statuses = {
			A: { icon: 'circle-check', color: 'primary', title: translate('partStatus.available', 'Available') },
			H: { icon: 'circle-check', color: 'warning', title: translate('partStatus.onHold', 'On Hold') },
			S: { icon: 'circle-dollar', color: 'success', title: translate('partStatus.sold', 'Sold') },
			D: { icon: 'circle-minus', color: 'danger', title: translate('partStatus.deleted', 'Deleted') },
			C: { icon: 'circle-minus', color: 'danger', title: translate('partStatus.crushed', 'Crushed') },
			T: { icon: 'circle-arrow-right', color: 'secondary', title: translate('partStatus.transferred', 'Transferred') },
		} as const
		if (item.kind === 'part' && item.part.status in statuses) {
			return statuses[item.part.status as keyof typeof statuses]
		}
		return {
			icon: 'circle-dashed',
			color: undefined,
			title: translate('common:templateItem', 'Template Item'),
		} as const
	}

	/** Totals for the existing parts currently shown in the list */
	function getTotals(filteredItems: Array<Item>) {
		return filteredItems.reduce(
			(totals, item) => {
				if (item.kind === 'part') {
					totals.retail = totals.retail.plus(item.part.retailPrice || '0')
					totals.cost = totals.cost.plus(item.part.cost || '0')
					totals.count += 1
				}
				return totals
			},
			{ retail: financialNumber('0'), cost: financialNumber('0'), count: 0 },
		)
	}

	async function onSelect(item: Item): Promise<LoadResult> {
		if (item.kind === 'slot') {
			return { kind: 'slot' }
		}
		if (!hasPermission('PARTS_CAN_VIEW_PARTS', item.part.storeId)) {
			return { kind: 'forbidden' }
		}
		try {
			// The sku of a part is its `inventoryId`
			return {
				kind: 'part',
				partData: await loadPartScreenData({ sku: item.part.inventoryId, storeId: item.part.storeId }),
			}
		} catch (err) {
			const message = Array.isArray(err) ? err[0].message : (err as Error).message
			mediator.publish('showMessage', {
				type: 'danger',
				heading: translate('vehicle.failedToLoadPartHeading', 'Failed to load part'),
				message,
				time: false,
			})
			return { kind: 'error', message }
		}
	}

	/** If the part can't be saved because it's missing required fields, the user has to choose between fixing it and throwing their changes away */
	function confirmDiscardUnsavedChanges(): boolean {
		return confirm(
			translate(
				'vehicle.teardownDiscardChanges',
				"This part has changes that can't be saved because required fields are missing. Discard the changes and continue?",
			),
		)
	}

	/** Saves the part being edited, if it has changes, before moving on to another item. If it can't be saved, the user stays where they are. */
	async function onBeforeSelect(item: Item): Promise<boolean> {
		if (!editor?.isDirty()) {
			return true
		}
		if (editor.hasMissingRequiredFields()) {
			return confirmDiscardUnsavedChanges()
		}
		keyAfterSave = item.key
		const saved = await editor.save()
		if (!saved) {
			keyAfterSave = undefined
		}
		// A successful save reloads the screen with the new item selected, so there's nothing left to do here
		return false
	}

	/** Saves the part, if it has changes, before the narrow-screen detail modal closes and unmounts the editor. If it can't be saved, the user can discard the changes or keep editing. */
	async function onBeforeCloseDetail(item: Item): Promise<boolean> {
		if (!editor?.isDirty()) {
			return true
		}
		if (editor.hasMissingRequiredFields()) {
			if (!confirmDiscardUnsavedChanges()) {
				return false
			}
			// The edits are still in the loaded part data, so reload it rather than reopen it later showing changes that were discarded
			keepDetailClosedOnReload = true
			reload(item.key)
			return false
		}
		keepDetailClosedOnReload = true
		const saved = await editor.save()
		if (!saved) {
			keepDetailClosedOnReload = false
		}
		// A successful save reloads the screen, which closes the modal
		return false
	}

	/** A save reloads the state (like the other entity screens do), so the selected item and template are kept in the URL for the new copy of the screen */
	function reload(selectedItem = selectedKey) {
		asr.go(
			null,
			{
				lastResetTime: Date.now(),
				selectedItem,
				templateId: templateId?.toString() ?? 'none',
			},
			{ replace: true, inherit: true },
		)
	}

	/** The state router can't wait for a save, so changes are saved first and then the move is tried again */
	export function canLeaveState(destination?: { name: string; parameters: Record<string, unknown> }): boolean {
		if (!editor?.isDirty()) {
			return true
		}
		if (editor.hasMissingRequiredFields()) {
			return confirmDiscardUnsavedChanges()
		}
		if (!destination) {
			return confirm(
				translate('vehicle.teardownUnsavedChangesLeave', 'You have unsaved changes. Are you sure you want to leave?'),
			)
		}
		// Set before saving, because a successful save calls `onSaved` (which would otherwise reload this screen) before it resolves
		navigatingAway = true
		void editor.save().then(saved => {
			if (saved) {
				asr.go(destination.name, destination.parameters)
			} else {
				navigatingAway = false
			}
		})
		return false
	}

	/** Creates a part for this vehicle, then reloads the screen with the new part selected. Resolves to false if the part wasn't created. */
	async function createPart(inventoryTypeId: number): Promise<boolean> {
		if (!canCreateParts || isCreatingPart) {
			return false
		}
		if (editor?.isDirty()) {
			if (editor.hasMissingRequiredFields() ? !confirmDiscardUnsavedChanges() : !(await editor.save())) {
				return false
			}
		}
		isCreatingPart = true
		try {
			const { data } = await createInventoryMutation.mutate({
				input: { inventoryTypeId, storeId: vehicle.storeId, vehicleId: vehicle.id },
			})
			if (!data) {
				throw new Error(translate('vehicle.failedToCreatePart', 'Failed to create the part.'))
			}
			reload(partKey({ storeId: data.createInventory.storeId, inventoryId: data.createInventory.inventoryId }))
			return true
		} catch (err) {
			isCreatingPart = false
			mediator.publish('showMessage', {
				type: 'danger',
				heading: translate('vehicle.failedToCreatePartHeading', 'Failed to create part'),
				message: Array.isArray(err) ? err[0].message : (err as Error).message,
				time: false,
			})
			return false
		}
	}

	const inventoryTypeMap = $derived(new Map(inventoryTypes.map(type => [type.id, type])))
	const activeInventoryTypes = $derived(inventoryTypes.filter(type => type.active))
	const components = $derived(templates.find(template => template.id === templateId)?.components ?? [])
	// The selected part stays listed, so that hiding unavailable parts doesn't close a part with unsaved changes
	const items = $derived(buildTeardownItems(vehicle.inventories, components, { hideUnavailable, keepKey: selectedKey }))
	const canCreateParts = $derived(
		hasPermission('PARTS_CAN_SAVE_PARTS', vehicle.storeId) && hasPermission('PARTS_CAN_EDIT_INFO', vehicle.storeId),
	)
</script>

<InventoryListBase
	{items}
	{getFilterText}
	{onSelect}
	{onBeforeSelect}
	{onBeforeCloseDetail}
	{openDetailOnMount}
	leftColumnHeightSubPx={420}
	rightColumnHeightSubPx={229}
	bind:this={list}
	bind:selectedKey
	canReuseResult={result => result.kind !== 'error'}
>
	{#snippet listHeader()}
		<TemplateSelect
			{templates}
			selectedTemplateId={templateId}
			onchange={async newTemplateId => {
				templateId = newTemplateId
				// The selected item might not be in the list anymore (e.g. an open slot from the old template)
				if (selectedKey && !items.some(item => item.key === selectedKey)) {
					await list?.selectItem(items[0]?.key ?? '')
				}
			}}
		/>
		<div class="mt-2">
			<Checkbox
				label={translate('vehicle.hideUnavailable', 'Hide Unavailable')}
				bind:checked={hideUnavailable}
			/>
		</div>
	{/snippet}

	{#snippet row({ item, isSelected, isLoading })}
		{@const { icon, color, title } = getStatusIcon(item)}
		<div class="d-flex justify-content-between align-items-start">
			<div class="text-ellipsis flex-grow-1">
				<Icon
					{icon}
					{title}
					{isLoading}
					prefix="far"
					class={['me-1', color && `text-${color}`]}
				/>
				<span
					class={{
						'fw-bold': item.kind === 'part' && (item.part.status === 'A' || item.part.status === 'H'),
						'fst-italic': item.kind === 'slot' || (item.kind === 'part' && item.part.status === 'S'),
					}}>{getInventoryTypeName(item.inventoryTypeId)}</span
				>
				{#if item.integral}
					<Badge
						color="secondary"
						class="ms-1"
						title={translate('part.integral', 'Integral')}>{translate('part.integral', 'Integral')}</Badge
					>
				{/if}
			</div>
			{#if item.kind === 'part' && item.part.tagNumber}
				<small class="flex-shrink-0">#{item.part.tagNumber}</small>
			{/if}
		</div>
		{#if item.kind === 'part' && (item.part.manufacturer?.name || item.part.model?.name)}
			<div>
				<small class:text-body-secondary={!isSelected}>
					{[item.part.manufacturer?.name, item.part.model?.name].filter(Boolean).join(' ')}
				</small>
			</div>
		{/if}
	{/snippet}

	{#snippet detail({ item, loadResult })}
		{#if item.kind === 'slot'}
			<div class="text-center py-5">
				<p class="text-body-secondary mb-3">
					{translate(
						'vehicle.teardownSlotHelp',
						"This part is part of the selected template, but it hasn't been created for this vehicle yet.",
					)}
				</p>
				<Button
					color="success"
					icon="check"
					disabled={!canCreateParts || isCreatingPart}
					isLoading={isCreatingPart}
					onclick={() => createPart(item.inventoryTypeId)}>{translate('common:createPart', 'Create Part')}</Button
				>
			</div>
		{:else if loadResult?.kind === 'error'}
			<p class="text-danger text-center py-5">
				{translate('vehicle.failedToLoadPart', 'This part could not be loaded.')}
				{loadResult.message}
			</p>
		{:else if loadResult?.kind === 'forbidden'}
			<p class="text-body-secondary fst-italic text-center py-5">
				{translate('vehicle.youDoNotHavePermissionToViewParts', 'You do not have permission to view this part.')}
			</p>
		{:else if loadResult?.kind === 'part'}
			{@const partData = loadResult.partData}
			{#key item.key}
				<PartEditor
					{...partData}
					embedded
					{asr}
					visibleTabs={['basic']}
					bind:this={editor}
					bind:part={partData.part}
					bind:attachments={partData.attachments}
					bind:typeFields={partData.typeFields}
					bind:origPart={partData.origPart}
					bind:optionValueMap={partData.optionValueMap}
					onSaved={() => {
						if (!navigatingAway) {
							reload(keyAfterSave ?? item.key)
						}
					}}
					onReload={() => reload(item.key)}
				/>
			{/key}
		{/if}
	{/snippet}

	{#snippet listFooter({ filteredItems })}
		{@const totals = getTotals(filteredItems)}
		<div class="d-flex justify-content-between">
			<span style="font-size: smaller;"
				><span class="fw-bold">{translate('common:retail', 'Retail')}:</span>
				{formatCurrency(totals.retail.toString())}</span
			>
			{#if canViewCost}
				<span style="font-size: smaller;"
					><span class="fw-bold">{translate('common:cost', 'Cost')}:</span>
					{formatCurrency(totals.cost.toString())}</span
				>
			{/if}
			<span style="font-size: smaller;"
				><span class="fw-bold">{translate('common:count', 'Count')}:</span> {totals.count}</span
			>
		</div>
		{#if canCreateParts}
			<div class="mt-2">
				<Button
					outline
					size="sm"
					icon="plus"
					color="success"
					disabled={isCreatingPart}
					onclick={() => newPartModal?.open()}>{translate('inventory.newPart', 'New Part')}...</Button
				>
			</div>
		{/if}
	{/snippet}

	{#snippet headerActions({ item })}
		{#if item.kind === 'slot'}
			<Button
				outline
				color="success"
				icon="check"
				disabled={!canCreateParts || isCreatingPart}
				onclick={() => createPart(item.inventoryTypeId)}>{translate('common:createPart', 'Create Part')}</Button
			>
		{/if}
	{/snippet}
</InventoryListBase>

<NewPartModal
	bind:this={newPartModal}
	inventoryTypes={activeInventoryTypes}
	{frequentlyUsedInventoryTypes}
	{createPart}
/>
